import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Col, Row, InputGroup } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const EmailConfigurationDetailsModal = ({
  initialPortDetails,
  initialSmtpServerDetails,
  initialSenderEmail,
  initialSenderEmailPassword,
  onSave,
  onClose,
  show,
  smtpFieldErrors = {},
  onClearSmtpError,
  isSaving = false, 
}) => {
  const { t } = useTranslation();
  const prevShowRef = useRef(false);

  const schema = yup.object({
    tempPortDetails: yup .string()
    .oneOf(["587", "465", "25"], t("validPortNumberValidationtext")) // Accepts only these three
    .required(t("portNumberValidationText")),
    tempSmtpServerDetails: yup.string().required(t("smtpServerValidationText")),
    tempSenderEmail: yup
      .string()
      .email(t("emailValidationText"))
      .required(t("emailRequiredValidationText")),
    tempSenderEmailPassword: yup
      .string()
      .required(t("passwordRequiredValidationText"))
      //HERE KEPT PASSWORD VALIDATIONS COMMENTED BECAUSE IT CAN BE USED FURTHER
      // .min(8, t("passwordMinValidationText"))
      // .matches(/[A-Z]/, t("passwordUppercaseValidationText"))
      // .matches(/[a-z]/, t("passwordLowercaseValidationText"))
      // .matches(/[0-9]/, t("passwordNumberValidationText"))
      // .matches(/[#?!@$%^&*-]/, t("passwordSpecialCharacterValidationText")),
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    tempSenderEmailPassword: false,
  });

  useEffect(() => {
    const justOpened = show && !prevShowRef.current;
    prevShowRef.current = show;

    if (justOpened) {
      reset({
        tempPortDetails: initialPortDetails || "",
        tempSmtpServerDetails: initialSmtpServerDetails || "",
        tempSenderEmail: initialSenderEmail || "",
        tempSenderEmailPassword: initialSenderEmailPassword || "",
      });
      setPasswordVisibility({ tempSenderEmailPassword: false });
    }
  }, [show, reset, initialPortDetails, initialSmtpServerDetails, initialSenderEmail, initialSenderEmailPassword]);

  const handleSave = (data) => {
    onSave(data);
    // no onClose() — parent closes on success only
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const clearBackendError = (fieldKey) => {
    if (smtpFieldErrors[fieldKey] && onClearSmtpError) {
      onClearSmtpError(fieldKey);
    }
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t("emailConfigurationModalHeaderText")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleSave)}>
          <Row>

            <Col md="12" className="mb-3">
              <Form.Label>
                {t("senderEmailText")}<span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  {...register("tempSenderEmail")}
                  isInvalid={!!errors.tempSenderEmail}
                  placeholder="sender@example.com"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tempSenderEmail?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            <Col md="12" className="mb-3">
              <Form.Label>
                {t("emailConfigPasswordText")}<span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={passwordVisibility.tempSenderEmailPassword ? "text" : "password"}
                  {...register("tempSenderEmailPassword")}
                  isInvalid={!!errors.tempSenderEmailPassword || !!smtpFieldErrors.tempSenderEmailPassword}
                  placeholder={t("password")}
                  onInput={() => clearBackendError("tempSenderEmailPassword")}
                />
                <InputGroup.Text
                  onClick={() => togglePasswordVisibility("tempSenderEmailPassword")}
                >
                  {passwordVisibility.tempSenderEmailPassword ? <FaEyeSlash /> : <FaEye />}
                </InputGroup.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.tempSenderEmailPassword?.message || smtpFieldErrors.tempSenderEmailPassword}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            <Col md="12" className="mb-3">
              <Form.Label>
                {t("smtpServerText")}<span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  {...register("tempSmtpServerDetails")}
                  isInvalid={!!errors.tempSmtpServerDetails || !!smtpFieldErrors.tempSmtpServerDetails}
                  placeholder="smtp.office365.com"
                  onInput={() => clearBackendError("tempSmtpServerDetails")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tempSmtpServerDetails?.message || smtpFieldErrors.tempSmtpServerDetails}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            <Col md="12" className="mb-3">
              <Form.Label>
                {t("portNumberText")}<span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  {...register("tempPortDetails")}
                  isInvalid={!!errors.tempPortDetails || !!smtpFieldErrors.tempPortDetails}
                  placeholder="587"
                  onInput={() => clearBackendError("tempPortDetails")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tempPortDetails?.message || smtpFieldErrors.tempPortDetails}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

          </Row>

          <Row className="justify-content-end">
            <Col xs="auto">
              <Button variant="secondary" size="sm" className="me-3" onClick={onClose}>
                {t("cancel")}
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={isSaving}>
                {isSaving ? t("saving") : t("save")}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EmailConfigurationDetailsModal;